<template>
  <div class="reporting-main-container">
    <ReportingInsightsSettings
      class="reporting-settings-container"
      @onStartTour="userTriggerStartTour"
      @dateRangeChange="handleDateRangeChange"
    />

    <div
      v-if="activeInsightSelectedGroupBy && activeInsightSelectedGroupBy.field"
      class="reporting-grid-data"
    >
      <ReportingGridHeader
        class="reporting-insights__grid-header"
        :selected-chart-type="selectedChartType"
        :selected-group-by="activeInsightSelectedGroupBy"
        :available-group-bys="availableGroupBys"
        @onChartTypeChange="onSelectChartType"
        @onGroupByChange="onGroupByChange"
        @onDeleteGroupBy="onDeleteGroupBy"
        @exportSelectCols="onExportFull(false)"
        @exportFull="onExportFull(true)"
        @openQuickInsightsToolPanel="toggleQuickInsightsToolPanel"
      />

      <ReportingGrid
        v-show="selectedChartType === 'table'"
        :group-by="activeInsightSelectedGroupBy"
        :open-columns-panel="openGridColumnToolPanel"
        :toggle-quick-insights-panel="toggleQuickInsightsPanel"
        :initial-expand-all-groups="initialExpandAllGroups"
        @onGridTopLevelDataLoaded="onGridTopLevelDataLoaded"
        @cellClicked="onCellClicked"
      />

      <ReportingCharts
        v-show="selectedChartType !== 'table'"
        class="m-10"
        :chart-type="selectedChartType"
        :group-by="activeInsightSelectedGroupBy"
        :selected-group-by="activeInsightSelectedGroupBy"
        @getChartDrilldownData="onChartDrilldown"
      />
    </div>

    <v-tour
      name="insightTour"
      :steps="steps"
      :options="{ highlight: true }"
      :callbacks="tourCallbacks"
    >
      <template slot-scope="tour">
        <transition name="fade">
          <v-step
            v-for="(step, index) of tour.steps"
            v-if="tour.currentStep === index"
            :key="index"
            :step="step"
            :previous-step="tour.previousStep"
            :next-step="tour.nextStep"
            :stop="tour.stop"
            :is-first="tour.isFirst"
            :is-last="tour.isLast"
            :labels="tour.labels"
            :highlight="tour.highlight"
          >
            <div
              slot="header"
              class="v-step__header"
            >
              <span />
              <span v-html="step.header ? step.header.title : ''" />
              <button @click.prevent="dontShowTourAgain(tour.currentStep)">
                <i class="fa fa-times" />
              </button>
            </div>
            <div slot="actions">
              <button
                v-if="!tour.isFirst"
                v-t="'TOUR_PREVIOUS'"
                class="btn btn-default btn-default--vue-tour btn-sm m-r-5"
                @click.prevent="tour.previousStep"
              />
              <button
                v-if="!tour.isLast"
                v-t="'TOUR_NEXT'"
                class="btn btn-primary btn-sm"
                @click.prevent="tour.nextStep"
              />
              <button
                v-if="tour.isLast"
                v-t="'TOUR_FINISH'"
                class="btn btn-primary btn-sm"
                @click.prevent="tour.finish"
              />
            </div>
          </v-step>
        </transition>
      </template>
    </v-tour>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import ReportingInsightsSettings from '../ReportingInsights/ReportingInsightsSettings';
import ReportingGridHeader from '../ReportingGridHeader';
import ReportingGrid from './ReportingGrid';
import ReportingCharts from './ReportingCharts';
import { INSIGHT_REPORT_TYPE } from '../../store/state';
import { filterService, userSettingsService, accountsService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import getInsightTourSteps from './insights-tour-steps';
import messages from './locales';
import analyticsMixinFactory from '@/core/mixins/analyticsMixinFactory';

const stepToGAEventNameMapping = {
  0: 'intro-insights-tour',
  1: 'list-parts-insights-tour',
  2: 'breakdown-levels-insights-tour',
  3: 'column-panel-insights-tour',
  4: 'filters-insights-tour',
  5: 'views-insights-tour',
  6: 'exports-insights-tour',
  7: 'saved-report-insights-tour',
  8: 'quick-insights-tour'
};

const TOUR_CATEGORY = 'insights-tour';

export default {
  name: 'ReportingInsights',
  components: {
    ReportingInsightsSettings,
    ReportingGridHeader,
    ReportingGrid,
    ReportingCharts
  },
  mixins: [analyticsMixinFactory('insights')],
  props: {
    savedInsightId: {
      type: String,
      default: ''
    },
    groupBys: {
      type: Array,
      default() {
        return [
          {
            field: 'campaignName',
            displayName: 'Campaign'
          }
        ];
      }
    },
    initialExpandAllGroups: {
      type: Boolean,
      default: false
    },
    columnState: {
      type: Array,
      default() {
        return [];
      }
    },
    quickDateRangeSelector: {
      type: String,
      default: 'Today'
    },
    cannedReportTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      completeGridData: null,
      steps: [...getInsightTourSteps(accountsService.hasAccess('CallLogFileExportJob', 'CREATE'))],
      tourCallbacks: {
        onNextStep: this.onNextStepHandler,
        onPreviousStep: this.onPreviousStepHandler,
        onFinish: this.onFinish,
        onStart: this.onStart
      },
      openGridColumnToolPanel: false,
      toggleQuickInsightsPanel: false
    };
  },
  computed: {
    ...mapGetters('reporting', [
      'activeInsight',
      'selectedChartType',
      'activeInsightSelectedGroupBy',
      'availableGroupBys'
    ])
  },
  async mounted() {
    this.getInsightsColDefs();

    this.getReportingTimezoneId({
      reportType: INSIGHT_REPORT_TYPE,
      reportId: this.savedInsightId
    });

    if (this.savedInsightId && this.savedInsightId !== 'unsaved') {
      await this.getInsight(this.savedInsightId);
    } else {
      this.setDefaultInsightActive();
      this.setDateRange({
        reportType: INSIGHT_REPORT_TYPE,
        dateRangeDetails: {
          quickRangeSelector: this.quickDateRangeSelector,
          isCustomRange: false
        }
      });

      this.setSelectedGroupBys({
        groupBys: this.groupBys,
        columnState: this.columnState
      });

      if (this.cannedReportTitle) {
        this.setReportName({
          reportType: INSIGHT_REPORT_TYPE,
          name: this.cannedReportTitle
        });
      }
    }

    this.getTags(INSIGHT_REPORT_TYPE);

    userSettingsService.getUserSettings().then(settings => {
      if (settings.showInsightsTour !== 'no') {
        this.$tours['insightTour'].start();
      }
    });
  },
  methods: {
    ...mapActions('reporting', [
      'getReportingTimezoneId',
      'setDefaultInsightActive',
      'getInsight',
      'exportInsightData',
      'getInsightsColDefs',
      'getTags',
      'setSelectedChartType',
      'setSelectedGroupBys',
      'updateBreakdownHeirarchy',
      'deleteBreakdownLevel',
      'setChartDefaultData',
      'updateDrilldownData',
      'addClickFilterCriteria',
      'getChartDrilldownData',
      'setDontShowInsightsTourAgain',
      'setDateRange',
      'setReportName',
      'resetAutoCompleteValues',
      'setCallLogsViewAs'
    ]),
    onGroupByChange(groupBy, index) {
      this.updateBreakdownHeirarchy({groupBy, index});
      this.trackEvent('change-breakdown-level', `${index}_${groupBy}`);
    },
    onDeleteGroupBy(index) {
      this.deleteBreakdownLevel(index);
      this.trackEvent('delete-breakdown-level', index);
    },
    onExportFull(allColumns) {
      this.exportInsightData({
        allColumns
      });
      this.trackEvent('export', allColumns ? 'all-columns' : 'displayed-columns');
    },
    onGridTopLevelDataLoaded({ data }) {
      this.completeGridData = data;
      this.setChartData(this.selectedChartType);
    },
    onSelectChartType(type) {
      this.setChartData(type);
      this.setSelectedChartType(type);
      this.trackEvent('change-chart-type', type);
    },
    setChartData(chartType) {
      if (chartType !== 'table') {
        this.setChartDefaultData({
          groupBy: this.activeInsightSelectedGroupBy.field,
          chartType: 'column',
          data: this.completeGridData
        });
        this.setChartDefaultData({
          groupBy: this.activeInsightSelectedGroupBy.field,
          chartType: 'pie',
          data: this.completeGridData
        });
        this.updateDrilldownData({
          groupBy: this.activeInsightSelectedGroupBy.field,
          data: this.completeGridData
        });
      }
    },
    onChartDrilldown({
      drilldowns,
      aggregateBy,
      chartPoint,
      chartType,
      initialGroupBy
    }) {
      this.getChartDrilldownData({
        drilldowns,
        aggregateBy,
        chartPoint,
        chartType,
        initialGroupBy,
        data: this.completeGridData
      });
    },

    onCellClicked(event) {
      const rowGroupColId = event.node.field;
      const filterCriteria = [];

      function addGroupFilter(rowGroupColId) {
        const userDefinedColDef = event.columnApi.getColumn(rowGroupColId).colDef;

        if (!userDefinedColDef.enableFilter && userDefinedColDef.type !== 'tag') {
          return;
        }

        const isTagColumnType = userDefinedColDef.type === 'tag';
        const filterGroupName = userDefinedColDef.filterGroupName;
        const newAndCondition = filterService.getNewAndFilterCondition();
        newAndCondition.tagCriteria[0].filterField = rowGroupColId;
        newAndCondition.tagCriteria[0].tagId = isTagColumnType
          ? rowGroupColId.replace('tag:', '')
          : filterGroupName + ':' + rowGroupColId;
        newAndCondition.tagCriteria[0].value = event.node.groupData['ag-Grid-AutoColumn'];
        filterCriteria.push(newAndCondition);
      }
      // grouping column clicked
      if (
        event.colDef &&
        event.colDef.ringbaAutoColumn &&
        event.event.target.className === 'ag-group-value'
      ) {
        addGroupFilter(rowGroupColId);
      }
      // value col clicked - only apply "filterable" cols
      else if (
        event.column &&
        event.column.aggFunc === 'count' &&
        filterService.getFilterableCol(event.column.colId)
      ) {
        if (rowGroupColId) {
          addGroupFilter(rowGroupColId);
        }

        const newAndCondition = filterService.getNewAndFilterCondition();
        const condition = filterService.getFilterableCol(event.column.colId);
        newAndCondition.tagCriteria[0] = {
          ...newAndCondition.tagCriteria[0],
          ...condition
        };
        filterCriteria.push(newAndCondition);
      }

      if (filterCriteria.length) {
        this.addClickFilterCriteria(filterCriteria);
        this.trackEvent('click-filter', rowGroupColId);
      }
    },

    dontShowTourAgain(currentStep) {
      this.setDontShowInsightsTourAgain();
      this.$tours['insightTour'].finish();
      this.trackEvent('never-show-again-insights-tour', stepToGAEventNameMapping[currentStep], TOUR_CATEGORY);
    },

    onNextStepHandler(step) {
      if (step === 2) {
        this.openGridColumnToolPanel = true;

        setTimeout(() => {
          this.openGridColumnToolPanel = false;
        }, 500);
      }

      this.trackEvent('view-step-tour', stepToGAEventNameMapping[step + 1], TOUR_CATEGORY);
    },

    onPreviousStepHandler(step) {
      if (step === 4) {
        this.openGridColumnToolPanel = true;

        setTimeout(() => {
          this.openGridColumnToolPanel = false;
        }, 500);
      }

      this.trackEvent('view-step-tour', stepToGAEventNameMapping[step - 1], TOUR_CATEGORY);
    },

    userTriggerStartTour() {
      this.$tours['insightTour'].start();
      this.trackEvent('user-trigger-insights-tour', undefined, TOUR_CATEGORY);
    },

    onFinish() {
      this.trackEvent('finish-insights-tour', undefined, TOUR_CATEGORY);
    },

    onStart() {
      this.trackEvent('start-insights-tour', undefined, TOUR_CATEGORY);
    },

    toggleQuickInsightsToolPanel() {
      this.toggleQuickInsightsPanel = !this.toggleQuickInsightsPanel;
      this.trackEvent('toggle-quick-insights', this.toggleQuickInsightsPanel);
    },

    handleDateRangeChange() {
      this.getTags(INSIGHT_REPORT_TYPE);
      this.resetAutoCompleteValues(INSIGHT_REPORT_TYPE);
    }
  },
  i18n: {
    locale: 'en',
    messages
  }
};
</script>

<style lang="scss" scoped>
.reporting-settings-container {
  margin-bottom: 20px;
}

.reporting-insights__grid-header {
  justify-content: flex-end;
}
</style>